import React, { useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import { 
  FiBold, FiItalic, FiUnderline, FiList, 
  FiAlignLeft, FiAlignCenter, FiAlignRight,
  FiLink, FiCornerDownLeft, FiType, FiDroplet
} from 'react-icons/fi';
import { HiSparkles } from 'react-icons/hi';

// Custom gold shimmer mark extension
const GoldShimmer = Highlight.extend({
  name: 'goldShimmer',
  addAttributes() {
    return {
      class: {
        default: 'gold-shimmer-text',
      },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', { ...HTMLAttributes, class: 'gold-shimmer-text' }, 0];
  },
  parseHTML() {
    return [{ tag: 'span.gold-shimmer-text' }];
  },
});

// Predefined colors palette
const COLOR_PALETTE = [
  { name: 'Or', value: '#d4af37' },
  { name: 'Blanc', value: '#ffffff' },
  { name: 'Noir', value: '#000000' },
  { name: 'Rouge', value: '#ef4444' },
  { name: 'Bleu', value: '#3b82f6' },
  { name: 'Vert', value: '#22c55e' },
  { name: 'Violet', value: '#8b5cf6' },
  { name: 'Rose', value: '#ec4899' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Gris', value: '#6b7280' },
];

// Toolbar button component
const ToolbarButton = ({ onClick, active, disabled, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-1.5 rounded transition-colors ${
      active 
        ? 'bg-amber-500 text-black' 
        : 'text-gray-400 hover:bg-gray-700 hover:text-white'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    {children}
  </button>
);

// Color picker dropdown
const ColorPicker = ({ editor, onClose }) => {
  return (
    <div className="absolute top-full left-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg p-2 shadow-xl z-50">
      <p className="text-xs text-gray-400 mb-2">Couleur du texte</p>
      <div className="grid grid-cols-5 gap-1">
        {COLOR_PALETTE.map((color) => (
          <button
            key={color.value}
            onClick={() => {
              editor.chain().focus().setColor(color.value).run();
              onClose();
            }}
            className="w-6 h-6 rounded border border-gray-600 hover:scale-110 transition-transform"
            style={{ backgroundColor: color.value }}
            title={color.name}
          />
        ))}
      </div>
      <button
        onClick={() => {
          editor.chain().focus().unsetColor().run();
          onClose();
        }}
        className="w-full mt-2 text-xs text-gray-400 hover:text-white py-1"
      >
        Supprimer la couleur
      </button>
    </div>
  );
};

// Main Rich Text Editor Component
const RichTextEditor = ({ 
  value = '', 
  onChange, 
  placeholder = 'Saisissez votre texte...',
  minHeight = '80px',
  maxHeight = '200px',
  simple = false, // Simple mode: only bold, italic, underline, line break
  className = ''
}) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: simple ? false : { levels: [1, 2, 3] },
        bulletList: !simple,
        orderedList: !simple,
        blockquote: !simple,
        codeBlock: false,
        code: false,
        horizontalRule: false,
      }),
      Underline,
      TextStyle,
      Color,
      Highlight.configure({
        multicolor: true,
        HTMLAttributes: {
          class: 'highlight-text',
        },
      }),
      GoldShimmer,
      ...(simple ? [] : [
        TextAlign.configure({
          types: ['heading', 'paragraph'],
        }),
        Link.configure({
          openOnClick: false,
          HTMLAttributes: {
            class: 'text-amber-500 underline',
          },
        }),
      ]),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Return empty string if content is just empty paragraph
      const isEmpty = html === '<p></p>' || html === '';
      onChange?.(isEmpty ? '' : html);
    },
    editorProps: {
      attributes: {
        class: `prose prose-sm prose-invert max-w-none focus:outline-none px-3 py-2 overflow-y-auto`,
        style: `min-height: ${minHeight}; max-height: ${maxHeight};`,
      },
    },
  });

  // Update editor content when value prop changes externally
  React.useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  if (!editor) return null;

  const addLink = () => {
    const url = window.prompt('URL du lien:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const insertLineBreak = () => {
    editor.chain().focus().setHardBreak().run();
  };

  const toggleGoldShimmer = () => {
    // Check if gold shimmer is active
    if (editor.isActive('goldShimmer')) {
      editor.chain().focus().unsetHighlight().run();
    } else {
      editor.chain().focus().setMark('goldShimmer').run();
    }
  };

  const toggleHighlight = () => {
    if (editor.isActive('highlight')) {
      editor.chain().focus().unsetHighlight().run();
    } else {
      editor.chain().focus().toggleHighlight({ color: '#fef08a' }).run();
    }
  };

  return (
    <div className={`border border-gray-600 rounded-lg overflow-hidden bg-gray-800 ${className}`}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 bg-gray-750 border-b border-gray-600 relative">
        {/* Text formatting */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
          title="Gras (Ctrl+B)"
        >
          <FiBold className="w-3.5 h-3.5" />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
          title="Italique (Ctrl+I)"
        >
          <FiItalic className="w-3.5 h-3.5" />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive('underline')}
          title="Souligné (Ctrl+U)"
        >
          <FiUnderline className="w-3.5 h-3.5" />
        </ToolbarButton>

        <div className="w-px h-4 bg-gray-600 mx-1" />

        {/* Color picker */}
        <div className="relative">
          <ToolbarButton
            onClick={() => setShowColorPicker(!showColorPicker)}
            active={showColorPicker}
            title="Couleur du texte"
          >
            <FiDroplet className="w-3.5 h-3.5" />
          </ToolbarButton>
          {showColorPicker && (
            <ColorPicker editor={editor} onClose={() => setShowColorPicker(false)} />
          )}
        </div>

        {/* Gold shimmer effect */}
        <ToolbarButton
          onClick={toggleGoldShimmer}
          active={editor.isActive('goldShimmer')}
          title="Effet doré animé ✨"
        >
          <HiSparkles className="w-3.5 h-3.5 text-amber-400" />
        </ToolbarButton>

        {/* Highlight */}
        <ToolbarButton
          onClick={toggleHighlight}
          active={editor.isActive('highlight')}
          title="Surligner en jaune"
        >
          <FiType className="w-3.5 h-3.5" />
        </ToolbarButton>

        <div className="w-px h-4 bg-gray-600 mx-1" />

        {/* Line break button - always visible */}
        <ToolbarButton
          onClick={insertLineBreak}
          title="Retour à la ligne (Shift+Enter)"
        >
          <FiCornerDownLeft className="w-3.5 h-3.5" />
        </ToolbarButton>

        {/* Extended toolbar for non-simple mode */}
        {!simple && (
          <>
            <div className="w-px h-4 bg-gray-600 mx-1" />

            {/* Lists */}
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              active={editor.isActive('bulletList')}
              title="Liste à puces"
            >
              <FiList className="w-3.5 h-3.5" />
            </ToolbarButton>

            <div className="w-px h-4 bg-gray-600 mx-1" />

            {/* Text alignment */}
            <ToolbarButton
              onClick={() => editor.chain().focus().setTextAlign('left').run()}
              active={editor.isActive({ textAlign: 'left' })}
              title="Aligner à gauche"
            >
              <FiAlignLeft className="w-3.5 h-3.5" />
            </ToolbarButton>
            
            <ToolbarButton
              onClick={() => editor.chain().focus().setTextAlign('center').run()}
              active={editor.isActive({ textAlign: 'center' })}
              title="Centrer"
            >
              <FiAlignCenter className="w-3.5 h-3.5" />
            </ToolbarButton>
            
            <ToolbarButton
              onClick={() => editor.chain().focus().setTextAlign('right').run()}
              active={editor.isActive({ textAlign: 'right' })}
              title="Aligner à droite"
            >
              <FiAlignRight className="w-3.5 h-3.5" />
            </ToolbarButton>

            <div className="w-px h-4 bg-gray-600 mx-1" />

            {/* Link */}
            <ToolbarButton
              onClick={addLink}
              active={editor.isActive('link')}
              title="Ajouter un lien"
            >
              <FiLink className="w-3.5 h-3.5" />
            </ToolbarButton>

            {editor.isActive('link') && (
              <ToolbarButton
                onClick={() => editor.chain().focus().unsetLink().run()}
                title="Supprimer le lien"
              >
                <span className="text-xs">✕</span>
              </ToolbarButton>
            )}
          </>
        )}
      </div>

      {/* Editor content */}
      <EditorContent 
        editor={editor} 
        className="rich-text-editor"
      />

      {/* Custom styles for the editor */}
      <style>{`
        .rich-text-editor .ProseMirror {
          min-height: ${minHeight};
          max-height: ${maxHeight};
          overflow-y: auto;
        }
        .rich-text-editor .ProseMirror p {
          margin: 0.25em 0;
        }
        .rich-text-editor .ProseMirror:focus {
          outline: none;
        }
        .rich-text-editor .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #6b7280;
          pointer-events: none;
          height: 0;
        }
        .rich-text-editor .ProseMirror ul {
          list-style: disc;
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        .rich-text-editor .ProseMirror ol {
          list-style: decimal;
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        .rich-text-editor .ProseMirror a {
          color: #d4af37;
          text-decoration: underline;
        }
        .rich-text-editor .ProseMirror .highlight-text {
          background-color: #fef08a;
          color: #000;
          padding: 0 2px;
          border-radius: 2px;
        }
        .rich-text-editor .ProseMirror .gold-shimmer-text {
          background: linear-gradient(90deg, #d4af37 0%, #fff9e6 50%, #d4af37 100%);
          background-size: 200% 100%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: shimmer 3s linear infinite;
          font-weight: bold;
        }
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .bg-gray-750 {
          background-color: #374151;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
